/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { debounceTask } from 'ember-lifeline';
import { subHours } from 'date-fns/subHours';
import { addHours } from 'date-fns/addHours';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { formatDateQuery } from 'uplisting-frontend/utils/date';
import { isSaveDisabled } from 'uplisting-frontend/utils/changeset';
import { arrayOf } from 'uplisting-frontend/utils/utils';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingPriceSignature {
  Element: HTMLDivElement;

  Args: IArgs;

  Blocks: {
    default: [];
  };
}

export default class UiBookingPriceComponent extends Component<BookingPriceSignature> {
  @service store!: Services['store'];
  @service session!: Services['session'];
  @service notifications!: Services['notifications'];

  @service('calendar/manager')
  calendarManager!: Services['calendar/manager'];

  @service('booking-drawer')
  bookingDrawer!: Services['booking-drawer'];

  numberOfGuestsOptions = arrayOf(30);

  @tracked isSubmitting = false;

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get isPricePermissible(): boolean {
    return this.currentUser.hasAccessTo('bookings.price.visible');
  }

  get isUpdatingPrice(): boolean {
    return this.bookingDrawer.isUpdatingPrice;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.bookingDrawer.changesetFor(this.booking);
  }

  @cached
  get checkInMaxDate(): Date {
    return subHours(this.changeset.checkOutDate, 12);
  }

  @cached
  get checkOutMinDate(): Date {
    return addHours(this.changeset.checkInDate, 12);
  }

  @action
  handleInsert(): void {
    const { dragOperation, resizeOperation } = this.calendarManager.interaction;

    if (dragOperation?.finished || resizeOperation?.finished) {
      debounceTask(this, 'syncStalePrice', 250);
    }
  }

  @action
  handleDateSelect(field: 'checkIn' | 'checkOut', date: Date): void {
    this.changeset[field] = formatDateQuery(date);

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  handleDatesSelect(date1: Date, date2: Date): void {
    this.changeset.checkIn = formatDateQuery(date1);
    this.changeset.checkOut = formatDateQuery(date2);

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  handleNumberOfGuestsChange(numberOfGuests: number): void {
    this.changeset.price.numberOfGuests = numberOfGuests;

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  @waitFor
  async handleFormSubmit(): Promise<void> {
    const { changeset, booking } = this;

    const saveDisabled = await isSaveDisabled(changeset);

    if (saveDisabled) {
      return;
    }

    const numberOfGuestsChanged = changeset.changes.find(
      (change) => change['key'] === 'price.numberOfGuests',
    );

    const { price } = booking;

    if (numberOfGuestsChanged) {
      price.hostEdited = true;
    }

    const { checkIn, checkOut } = changeset;
    const { dragOperation, dragChangedMultiUnit } =
      this.calendarManager.interaction;

    try {
      this.isSubmitting = true;

      if (dragChangedMultiUnit && dragOperation) {
        booking.newMultiUnit =
          dragOperation.location as ModelRegistry['multi-unit'];

        await booking.save();

        booking.newMultiUnit = undefined;
      }

      price.checkIn = checkIn;
      price.checkOut = checkOut;

      await price.save();

      price.dropOrphanedExtraPrices();

      await booking.reload();

      this.calendarManager.interaction.clearOperations();

      this.notifications.info();
    } catch {
      this.notifications.error();

      price.rollbackAttributes();
      booking.rollbackAttributes();
    } finally {
      this.isSubmitting = false;
    }
  }

  private async syncPrice(): Promise<void> {
    await this.bookingDrawer.requote(this.booking, this.changeset);
  }

  private async syncStalePrice(): Promise<void> {
    await this.bookingDrawer.requoteIfStale(this.booking, this.changeset);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price': typeof UiBookingPriceComponent;
  }
}
